import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { calcBillingRate, calcBillingRatesData, type BillingRatesInput } from "@/lib/billing-rates-calc";

export interface ProjectPhaseStatus {
  project_id: string;
  project_name: string;
  project_code: string | null;
  project_phase: string | null;
}

export interface ConsultantStats {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  total_hours: number;
  budgeted_hours: number;
  billing_rate: number;
}

export interface ProductionKPIs {
  activeProjects: number;
  totalHoursThisMonth: number;
  totalBudgetedHours: number;
  teamBillingRate: number;
  projectPhases: ProjectPhaseStatus[];
  consultantStats: ConsultantStats[];
}

export function useProductionKPIs() {
  return useQuery({
    queryKey: ["production-kpis"],
    queryFn: async (): Promise<ProductionKPIs> => {
      const now = new Date();
      const year = now.getFullYear();
      const yearStart = `${year}-01-01`;
      const monthStart = new Date(year, now.getMonth(), 1).toISOString();

      const [projectsRes, timeEntries, usersRes, billableAssignments, absencesRes] =
        await Promise.all([
          supabase
            .from("projects")
            .select("id, name, project_code, project_phase, is_active, project_type")
            .eq("is_active", true)
            .is("deleted_at", null),
          // VIB-946: team-wide unbounded — paginate to avoid 1000-row silent truncation.
          fetchAllPaginated<{
            user_id: string;
            duration_seconds: number | null;
            budgeted_hours: number | null;
            start_date: string;
            budget_line_id: string | null;
            project_id: string | null;
          }>(
            "time_entries",
            "user_id, duration_seconds, budgeted_hours, start_date, budget_line_id, project_id",
            (q) => q.is("deleted_at", null).gte("start_date", monthStart),
          ),
          supabase
            .from("users")
            .select("id, full_name, avatar_url, monthly_capacity_hours, target_billing_rate")
            .eq("is_active", true),
          // HIGH-1: authoritative billable assignments for team billing rate
          // Erik-lock 2026-06-02: ekskluder soft-deletede og kansellerte prosjekter.
          // VIB-946: team-wide unbounded — paginate to avoid 1000-row silent truncation.
          fetchAllPaginated<{
            id: string;
            user_id: string;
            hours: number | null;
            task_status: string;
            budget_line_id: string;
            completed_at: string | null;
            approved_at: string | null;
          }>(
            "budget_line_assignments",
            "id, user_id, hours, task_status, budget_line_id, completed_at, approved_at, budget_lines!inner(project_id, cost_type, quantity_hours, projects!inner(deleted_at, project_status, project_type))",
            (q) =>
              q
                .in("task_status", ["completed", "approved"])
                .is("budget_lines.projects.deleted_at", null)
                .neq("budget_lines.projects.project_status", "cancelled"),
          ),
          // VIB-2915: kapasitet uten fraværstype, så TV-skjermen kan lese den.
          supabase.rpc("absence_capacity", { p_from: yearStart, p_to: `${year}-12-31` }),
        ]);

      const projects = projectsRes.data ?? [];
      const users = usersRes.data ?? [];
      const absences = absencesRes.data ?? [];

      // Active projects
      const activeProjects = projects.length;

      // Total hours this month (loggede timer — for timeforbruk-visning)
      const totalHoursThisMonth = timeEntries.reduce(
        (s, t) => s + ((t.duration_seconds ?? 0) / 3600),
        0,
      );

      // Total budgeted hours — alle assignments for aktive prosjekter (timeforbruk vs budsjett)
      const totalBudgetedHours = billableAssignments.reduce(
        (s, a) => s + (a.hours ?? 0),
        0,
      );

      // VIB-916: helligdager reduserer kapasitet (globalt).
      const { data: holidayRows } = await supabase
        .from("company_holidays")
        .select("date")
        .gte("date", yearStart)
        .lt("date", `${year + 1}-01-01`);

      // VIB-923: T&M-prosjekter — billable = førte timer (se use-billing-rates).
      const hourlyIds = new Set(
        projects.filter((p) => p.project_type === "hourly").map((p) => p.id),
      );
      const hourlyEntries = timeEntries
        .filter((te) => te.project_id && hourlyIds.has(te.project_id))
        .map((te) => ({
          user_id: te.user_id,
          duration_seconds: te.duration_seconds,
          start_date: te.start_date,
        }));

      // HIGH-1: teamBillingRate — autoritativ SOT-formel via calcBillingRatesData.
      // Bruker inneværende måneds rate fra team-aggregeringen.
      const billingData = calcBillingRatesData({
        users: users as BillingRatesInput["users"],
        billableAssignments: billableAssignments as unknown as BillingRatesInput["billableAssignments"],
        timeEntries: timeEntries as BillingRatesInput["timeEntries"],
        absences: absences as BillingRatesInput["absences"],
        confirmations: [],
        hourlyEntries,
        holidays: (holidayRows ?? []).map((h: { date: string }) => h.date),
        year,
        now,
      });

      const currentMonth = now.getMonth() + 1;
      let teamBillableThisMonth = 0;
      let teamCapacityThisMonth = 0;
      for (const emp of billingData.employees) {
        const m = emp.months.find((mo) => mo.month === currentMonth);
        if (m) {
          teamBillableThisMonth += m.billableHours;
          teamCapacityThisMonth += m.capacity;
        }
      }
      const teamBillingRate = calcBillingRate(teamBillableThisMonth, teamCapacityThisMonth) ?? 0;

      // Project phases for timeline
      const projectPhases: ProjectPhaseStatus[] = projects
        .filter((p) => p.project_phase)
        .map((p) => ({
          project_id: p.id,
          project_name: p.name,
          project_code: p.project_code,
          project_phase: p.project_phase,
        }));

      // Consultant stats (timer logget denne måneden per bruker)
      const userHoursMap = new Map<string, number>();
      for (const t of timeEntries) {
        if (t.user_id) {
          userHoursMap.set(
            t.user_id,
            (userHoursMap.get(t.user_id) ?? 0) + ((t.duration_seconds ?? 0) / 3600),
          );
        }
      }
      const userBudgetMap = new Map<string, number>();
      for (const a of billableAssignments) {
        userBudgetMap.set(a.user_id, (userBudgetMap.get(a.user_id) ?? 0) + (a.hours ?? 0));
      }

      // HIGH-1: per-konsulent billing_rate skal være SAMME SOT-faktureringsgrad
      // som teamraten og konsulentens egen dashboard (fakturerbare timer fra
      // godkjente oppgaver / kapasitet), ikke loggede timer. Hentes fra
      // calcBillingRatesData per ansatt for inneværende måned.
      const sotRateByUser = new Map<string, number>();
      for (const emp of billingData.employees) {
        const m = emp.months.find((mo) => mo.month === currentMonth);
        sotRateByUser.set(emp.userId, m?.rate != null ? Math.round(m.rate) : 0);
      }

      const consultantStats: ConsultantStats[] = users
        .map((u) => {
          const totalHours = Math.round((userHoursMap.get(u.id) ?? 0) * 10) / 10;
          const budgeted = userBudgetMap.get(u.id) ?? 0;
          return {
            user_id: u.id,
            full_name: u.full_name,
            avatar_url: u.avatar_url,
            total_hours: totalHours,
            budgeted_hours: budgeted,
            billing_rate: sotRateByUser.get(u.id) ?? 0,
          };
        })
        .filter((c) => c.total_hours > 0 || c.budgeted_hours > 0)
        .sort((a, b) => b.total_hours - a.total_hours);

      return {
        activeProjects,
        totalHoursThisMonth: Math.round(totalHoursThisMonth * 10) / 10,
        totalBudgetedHours,
        teamBillingRate,
        projectPhases,
        consultantStats,
      };
    },
    refetchInterval: 60_000, // Refresh every minute for display
    staleTime: 30_000,
  });
}
